import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toAddressInput } from "./driveTimeFormat";
import { useTourModal } from "../../tours/tourModals";

/**
 * Drive-time pins (2026-10-09): up to TWO pinned addresses, "start" and "end",
 * that every address surface in the admin app can compare against.
 *
 * THE WORKFLOW. A slot opens between job X and job Y. The office pins X as
 * start and Y as end, then asks any candidate address "how far from X, how far
 * to Y". One pin works too (one leg).
 *
 * NO TIMER, DELIBERATELY. The first idea was a five-minute pin that rode on
 * the copy button. Copy is used for other reasons (texting a refinisher an
 * address), and a timed pin vanishes mid-task. Pins stay until cleared,
 * replaced, or logout, and the navbar chip shows what is pinned at all times.
 *
 * STORAGE. localStorage, so the calendar in one window and a client page in
 * another see the same pins; the `storage` event keeps tabs in step. Reads and
 * writes are wrapped — a private window or blocked storage degrades to
 * per-tab pins, never to a crash. Cleared when `active` drops (logout).
 */
const STORAGE_KEY = "bdr-drive-time-pins";
const EMPTY = { start: null, end: null };

const sanitizePlace = (p) => {
  if (!p || typeof p !== "object") return null;
  const address = toAddressInput(p.address);
  const text = [address.streetAddress, address.city, address.state, address.zipCode]
    .filter(Boolean)
    .join("");
  if (!text) return null;
  return {
    label: String(p.label || address.streetAddress || "Pinned address"),
    address,
    pinnedAtMs: Number(p.pinnedAtMs) || Date.now(),
  };
};

/*
 * `demoSaved` is the user's real pins while a walkthrough's DEMO pin is in
 * place (see beginDemo). A tab closed mid-walkthrough would otherwise keep
 * the demo pin and lose the real ones; on the next read the saved pins win
 * and the demo is gone — a tour can never be running on a fresh mount.
 */
const readStored = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const v = JSON.parse(raw);
    const src = v && v.demoSaved && typeof v.demoSaved === "object" ? v.demoSaved : v;
    return { start: sanitizePlace(src?.start), end: sanitizePlace(src?.end) };
  } catch {
    return EMPTY;
  }
};

const writeStored = (pins, demoSaved) => {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(demoSaved ? { ...pins, demoSaved } : pins)
    );
  } catch {
    /* storage blocked — pins live in memory for this tab */
  }
};

/** Build a pin from a label and anything carrying the four address fields. */
export const makePlace = (label, addressLike) => ({
  label: String(label || "").trim() || String(addressLike?.streetAddress || "Pinned address"),
  address: toAddressInput(addressLike),
  pinnedAtMs: Date.now(),
});

const INERT = {
  start: null,
  end: null,
  hasAny: false,
  demo: false,
  pinStart: () => {},
  pinEnd: () => {},
  clearStart: () => {},
  clearEnd: () => {},
  clearAll: () => {},
  beginDemo: () => {},
  endDemo: () => {},
};

const DriveTimePinContext = createContext(null);

export const DriveTimePinProvider = ({ active = true, children }) => {
  const [pins, setPins] = useState(readStored);
  /*
   * Walkthrough demo (registry §17.10, guide §5): `beginDemo(place)` parks
   * the user's real pins and shows `place` as the start; `endDemo()` puts
   * them back. Registered as the tagged surface "drive-time-demo" HERE, at
   * App level, so the registration survives the tour's route changes and
   * the engine's end-of-tour sweep restores the pins wherever the user
   * exits (a page-level registration would unmount on navigation and the
   * demo pin would silently become a real one).
   */
  const [demo, setDemo] = useState(false);
  const savedRef = useRef(null);

  useEffect(() => {
    writeStored(pins, demo ? savedRef.current || EMPTY : null);
  }, [pins, demo]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setPins(readStored());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /* Logout clears the pins: a shared office machine must not hand the next
     login the previous person's workflow. Only the true→false edge — mounting
     inactive (the public site) must not wipe a pin set in another tab. */
  const wasActive = useRef(active);
  useEffect(() => {
    if (wasActive.current && !active) {
      savedRef.current = null;
      setDemo(false);
      setPins(EMPTY);
    }
    wasActive.current = active;
  }, [active]);

  const endDemo = () => {
    const saved = savedRef.current;
    savedRef.current = null;
    setDemo(false);
    setPins(saved || EMPTY);
  };
  useTourModal("drive-time-demo", demo, endDemo);

  const api = useMemo(
    () => ({
      start: pins.start,
      end: pins.end,
      hasAny: !!(pins.start || pins.end),
      demo,
      pinStart: (place) =>
        setPins((prev) => ({ ...prev, start: sanitizePlace(place) })),
      pinEnd: (place) => setPins((prev) => ({ ...prev, end: sanitizePlace(place) })),
      clearStart: () => setPins((prev) => ({ ...prev, start: null })),
      clearEnd: () => setPins((prev) => ({ ...prev, end: null })),
      clearAll: () => setPins(EMPTY),
      /* Safe to call twice (a tour re-runs an action on Back): the real
         pins are parked only the first time. */
      beginDemo: (place) => {
        setPins((prev) => {
          if (!savedRef.current) savedRef.current = prev;
          return { start: sanitizePlace(place), end: null };
        });
        setDemo(true);
      },
      endDemo,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pins, demo]
  );

  return (
    <DriveTimePinContext.Provider value={api}>{children}</DriveTimePinContext.Provider>
  );
};

/** Inert outside the provider, so a surface renders (with no pins) in isolation. */
export const useDriveTimePins = () => useContext(DriveTimePinContext) || INERT;

export { STORAGE_KEY as DRIVE_TIME_PINS_STORAGE_KEY };
